import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import * as PDFJS from 'pdfjs-dist';
import { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist/types/src/pdf';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, RowClickedEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import PdfPageCellRenderer from './PdfPageCellRenderer';
import styles from './pdfPreviewSideSlide.module.css';
import { STATUS_MESSAGES } from '../../utils/messages';

function fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = (ev) => reject(ev.target?.error);
    reader.onload = (ev) => resolve(ev.target?.result as ArrayBuffer);
    reader.readAsArrayBuffer(file);
  });
}

async function loadPdf(pdfFile: File): Promise<PDFDocumentProxy> {
  const buffer = await fileToArrayBuffer(pdfFile);
  return PDFJS.getDocument({
    data: buffer,
    cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS.version}/cmaps/`,
    cMapPacked: true,
  }).promise as Promise<PDFDocumentProxy>;
}

interface PdfPreviewProps {
  className?: string;
  pdfFile: File | null | undefined;
  style?: React.CSSProperties;
  setCurrentPage?: (page: number) => void;
  currentPage?: number;
}

interface PdfPageRow {
  pageNumber: number;
  page: PDFPageProxy;
}

export default function PdfPreviewSideSlide({
  className, pdfFile, style, setCurrentPage, currentPage,
}: PdfPreviewProps) {
  // states
  const [rowData, setRowData] = useState<PdfPageRow[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const selectedPage = currentPage ?? 1;

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      field: 'pageNumber',
      headerName: 'Pages',
      cellRenderer: PdfPageCellRenderer,
      width: 100,
      height: 130,
      cellStyle: { marginLeft: '5px' },
      resizable: false,
      sortable: false,
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    resizable: false,
    sortable: false,
  }), []);

  // methods
  const loadPages = useCallback((pdf: PDFDocumentProxy) => Promise.all(
    Array.from({ length: pdf.numPages }, (_, i) => pdf.getPage(i + 1)),
  ), []);

  const refreshPdf = useCallback(async () => {
    if (!pdfFile) {
      setRowData([]);
      return;
    }

    try {
      const pdf = await loadPdf(pdfFile);
      const pages = await loadPages(pdf);

      const newRowData = pages.map((page, index) => ({
        pageNumber: index + 1,
        page,
      }));

      setRowData(newRowData);
    } catch (error) {
      setRowData([]);
    }
  }, [loadPages, pdfFile]);

  const onRowClicked = useCallback((event: RowClickedEvent<PdfPageRow>) => {
    if (event.data) {
      setCurrentPage?.(event.data.pageNumber);
    }
  }, [setCurrentPage]);

  const onGridReady = useCallback((params: any) => {
    setGridApi(params.api);
  }, []);

  // effects
  useEffect(() => {
    refreshPdf();
  }, [pdfFile, refreshPdf]);

  useEffect(() => {
    if (gridApi && selectedPage) {
      gridApi.forEachNode((node: any) => {
        if (node.data?.pageNumber === selectedPage) {
          node.setSelected(true, false);
          gridApi.ensureNodeVisible(node, 'middle');
        } else {
          node.setSelected(false, false);
        }
      });
    }
  }, [selectedPage, gridApi, rowData]);

  return (
    <div
      className={[styles.container, className].join(' ')}
      style={{
        ...style,
        width: '100%',
        height: '100%',
      }}
    >
      <div className="ag-theme-alpine" style={{ height: '100%' }}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onRowClicked={onRowClicked}
          onGridReady={onGridReady}
          rowHeight={156}
          headerHeight={0}
          suppressRowClickSelection={false}
          rowSelection="single"
          getRowId={(params) => params.data.pageNumber.toString()}
          alwaysShowVerticalScroll
          suppressContextMenu
          overlayNoRowsTemplate={STATUS_MESSAGES.LOADING}
        />
      </div>
    </div>
  );
}
